import { useEffect, useState } from "react";
import { API, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Printer, ClipboardCheck } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";
import DocumentShareButton from "@/components/DocumentShareButton";
import DocumentIdentityFields from "@/components/DocumentIdentityFields";
import { documentIdentityFormValues, resolveDocumentIdentity } from "@/lib/documentIdentity";

const EMPTY = { customer_name: "", customer_address: "", sj_ref: "", items: [{ description: "", qty: 1, unit: "pcs" }], date: new Date().toISOString().slice(0, 10), notes: "", business_name: "", business_logo: "", business_whatsapp: "" };

export default function TandaTerima() {
  const [list, setList] = useState(null);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [printing, setPrinting] = useState(null);
  const [landscape, setLandscape] = useState(false);
  const [site, setSite] = useState(null);

  const load = () => API.get("/tanda-terima").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); API.get("/settings").then((r) => setSite(r.data)).catch(() => {}); }, []);

  const save = async () => {
    try {
      const payload = { ...form, items: form.items.filter((i) => i.description).map((i) => ({ ...i, qty: Number(i.qty) || 0 })) };
      if (editId) {
        await API.put(`/tanda-terima/${editId}`, payload);
        toast.success("Surat tanda terima diperbarui");
      } else {
        await API.post("/tanda-terima", payload);
        toast.success("Surat tanda terima dibuat");
      }
      setForm(null);
      setEditId(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus surat tanda terima ini?")) return;
    await API.delete(`/tanda-terima/${id}`);
    toast.success("Surat tanda terima dihapus");
    load();
  };

  const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";
  const labelCls = "text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5";
  const printIdentity = resolveDocumentIdentity(printing || {}, site || {});

  return (
    <div data-testid="admin-tanda-terima">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0B1F3A]">Surat Tanda Terima Barang</h1>
          <p className="text-sm text-slate-500">Bukti serah terima — dibuat untuk konsumen yang sudah menerima barangnya</p>
        </div>
        <button onClick={() => { setForm({ ...EMPTY, ...documentIdentityFormValues({}, site || {}), items: [{ description: "", qty: 1, unit: "pcs" }] }); setEditId(null); }} data-testid="add-ttb" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0B1F3A] bg-navy-hover text-white text-sm font-bold rounded-md transition-colors">
          <Plus className="h-4 w-4" /> Buat Tanda Terima
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="ttb-table">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-400">
              <th className="px-5 py-3">No. TTB</th>
              <th className="px-5 py-3">Penerima</th>
              <th className="px-5 py-3">No. Surat Jalan</th>
              <th className="px-5 py-3">Tanggal Terima</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(list || []).map((t) => (
              <tr key={t.id} data-testid={`ttb-row-${t.id}`} className="hover:bg-slate-50">
                <td className="px-5 py-3 font-mono-accent text-xs font-bold">{t.ttb_number}</td>
                <td className="px-5 py-3">{t.customer_name}<span className="block text-xs text-slate-400 max-w-[220px] truncate">{t.customer_address}</span></td>
                <td className="px-5 py-3">{t.sj_ref || "-"}</td>
                <td className="px-5 py-3 text-xs text-slate-500">{t.date}</td>
                <td className="px-5 py-3 whitespace-nowrap">
                  <button onClick={() => setPrinting(t)} data-testid={`ttb-print-${t.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0B1F3A] text-white text-xs font-bold rounded-md hover:bg-[#132D52] transition-colors">
                    <Printer className="h-3.5 w-3.5" /> Cetak
                  </button>
                  <button onClick={() => { setForm({ customer_name: t.customer_name, customer_address: t.customer_address || "", sj_ref: t.sj_ref || "", items: t.items, date: t.date, notes: t.notes || "", ...documentIdentityFormValues(t, site || {}) }); setEditId(t.id); }} data-testid={`ttb-edit-${t.id}`} className="ml-2 p-1.5 text-slate-400 hover:text-[#0B1F3A]"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(t.id)} data-testid={`ttb-delete-${t.id}`} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {list && list.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400"><ClipboardCheck className="h-8 w-8 mx-auto mb-2 text-slate-300" />Belum ada surat tanda terima.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setForm(null)} />
          <div className="relative bg-white rounded-md border border-slate-200 w-full max-w-xl max-h-[90vh] overflow-y-auto p-6" data-testid="ttb-dialog">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold">{editId ? "Edit Surat Tanda Terima" : "Buat Surat Tanda Terima Barang"}</h2>
              <button onClick={() => setForm(null)} data-testid="ttb-close-dialog"><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="space-y-4">
              <DocumentIdentityFields value={form} onChange={setForm} settings={site || {}} testIdPrefix="ttb" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Nama Penerima *</label>
                  <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="ttb-customer" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>No. Surat Jalan (opsional)</label>
                  <input value={form.sj_ref} onChange={(e) => setForm({ ...form, sj_ref: e.target.value })} data-testid="ttb-sj-ref" className={inputCls} placeholder="Mis: SJ-202609-0001" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Alamat Penerima</label>
                <textarea rows={2} value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} data-testid="ttb-address" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Tanggal Terima</label>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="ttb-date" className={inputCls} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={labelCls + " mb-0"}>Barang yang Diterima</label>
                  <button onClick={() => setForm({ ...form, items: [...form.items, { description: "", qty: 1, unit: "pcs" }] })} data-testid="ttb-add-item" className="text-xs font-bold text-[#C9A227] hover:underline">
                    + Tambah Barang
                  </button>
                </div>
                <div className="space-y-2">
                  {form.items.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <input value={it.description} onChange={(e) => { const arr = [...form.items]; arr[i] = { ...it, description: e.target.value }; setForm({ ...form, items: arr }); }} data-testid={`ttb-item-desc-${i}`} className={inputCls + " col-span-6"} placeholder="Nama barang + varian" />
                      <input type="number" min="1" value={it.qty} onChange={(e) => { const arr = [...form.items]; arr[i] = { ...it, qty: e.target.value }; setForm({ ...form, items: arr }); }} data-testid={`ttb-item-qty-${i}`} className={inputCls + " col-span-3"} placeholder="Qty" />
                      <input value={it.unit} onChange={(e) => { const arr = [...form.items]; arr[i] = { ...it, unit: e.target.value }; setForm({ ...form, items: arr }); }} data-testid={`ttb-item-unit-${i}`} className={inputCls + " col-span-2"} placeholder="Satuan" />
                      <button onClick={() => setForm({ ...form, items: form.items.filter((_, x) => x !== i) })} className="col-span-1 text-red-500"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label className={labelCls}>Catatan</label>
                <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="ttb-notes" className={inputCls} />
              </div>
              <button onClick={save} disabled={!form.customer_name} data-testid="save-ttb" className="w-full px-6 py-3 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors disabled:opacity-50">
                Simpan Tanda Terima
              </button>
            </div>
          </div>
        </div>
      )}

      {printing && (
        <div className="document-print-overlay fixed inset-0 z-50 overflow-y-auto bg-slate-100 p-4 sm:p-8">
          <style>{`@page { size: A4 ${landscape ? "landscape" : "portrait"}; margin: 10mm; }`}</style>
          <div className={`no-print flex items-center justify-between ${landscape ? "max-w-6xl" : "max-w-3xl"} mx-auto mb-4 gap-3`}>
            <button onClick={() => setPrinting(null)} data-testid="ttb-print-close" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#0B1F3A]">
              <X className="h-4 w-4" /> Tutup
            </button>
            <div className="flex flex-wrap justify-end gap-2">
              <div className="flex gap-2" data-testid="ttb-orientation-toggle">
                {[{ v: false, l: "Potret" }, { v: true, l: "Lanskap" }].map((orientation) => (
                  <button
                    key={orientation.l}
                    type="button"
                    onClick={() => setLandscape(orientation.v)}
                    data-testid={`ttb-orient-${orientation.l.toLowerCase()}`}
                    aria-pressed={landscape === orientation.v}
                    className={`px-4 py-2.5 text-xs font-bold rounded-md border transition-colors ${landscape === orientation.v ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : "bg-white border-slate-300 text-slate-600 hover:border-[#0B1F3A]"}`}
                  >
                    {orientation.l}
                  </button>
                ))}
              </div>
              <DocumentShareButton
                filename={`${printing.ttb_number || "tanda-terima-nawadah"}.pdf`}
                title={`Tanda Terima ${printing.ttb_number}`}
                message={`Halo ${printing.customer_name}, berikut Surat Tanda Terima ${printing.ttb_number} dari ${printIdentity.name}.`}
                lines={[
                  { text: printIdentity.name, size: 12, bold: true, align: "center" },
                  { text: `WhatsApp: ${printIdentity.whatsapp}`, size: 9, align: "center", muted: true },
                  { text: "SURAT TANDA TERIMA BARANG", size: 20, bold: true, align: "center", gap: 4 },
                  { text: printing.ttb_number, size: 12, bold: true, align: "center", gap: 12 },
                  { rule: true },
                  { text: `Tanggal diterima: ${printing.date}` },
                  { text: `Diterima oleh: ${printing.customer_name}`, bold: true },
                  { text: `Alamat: ${printing.customer_address || "-"}` },
                  { text: `Referensi Surat Jalan: ${printing.sj_ref || "-"}`, gap: 8 },
                  { text: "BARANG YANG DITERIMA", size: 11, bold: true },
                  { rule: true },
                  ...printing.items.map((item, index) => ({ text: `${index + 1}. ${item.description} - ${item.qty} ${item.unit}`, gap: 3 })),
                  { rule: true },
                  { text: "Barang telah diterima dengan baik, lengkap, dan dalam kondisi baik.", gap: 8 },
                  ...(printing.notes ? [{ text: `Catatan: ${printing.notes}`, gap: 10 }] : []),
                  { text: "", spacer: true, gap: 24 },
                  { text: `Yang menyerahkan: ${printIdentity.name}` },
                  { text: `Yang menerima: ${printing.customer_name}` },
                ]}
                testid="share-ttb-whatsapp"
              />
              <button onClick={() => window.print()} data-testid="ttb-do-print" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0B1F3A] text-white text-sm font-bold rounded-md">
                <Printer className="h-4 w-4" /> Cetak / Simpan PDF
              </button>
            </div>
          </div>
          <div className={`print-area document-print-sheet ${landscape ? "document-print-sheet--landscape max-w-6xl" : "max-w-3xl"} bg-white border border-slate-200 rounded-md mx-auto p-10 print:max-w-full print:border-0`} data-testid="ttb-print-area" data-orientation={landscape ? "landscape" : "portrait"}>
            <div className="flex items-start justify-between pb-6 border-b-2 border-[#0B1F3A]">
              <div className="flex items-center gap-4">
                <img src={printIdentity.logo} alt={`Logo ${printIdentity.name}`} className="h-16 w-16 object-contain print:h-24 print:w-24" />
                <div className="max-w-[260px]">
                  <p className="font-display text-xl font-bold leading-tight text-[#0B1F3A]">{printIdentity.name}</p>
                  <p className="text-xs text-slate-500 mt-1">Indonesia • WA: {printIdentity.whatsapp}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-display text-2xl font-bold text-[#0B1F3A]">SURAT TANDA TERIMA BARANG</p>
                <p className="font-mono-accent text-sm font-bold">{printing.ttb_number}</p>
                <p className="text-xs text-slate-500">Tanggal: {printing.date}</p>
              </div>
            </div>
            <p className="py-5 text-sm text-slate-600">Yang bertanda tangan di bawah ini menyatakan bahwa barang berikut telah <strong>diterima dengan baik, lengkap, dan dalam kondisi baik</strong> oleh penerima:</p>
            <div className="grid grid-cols-2 gap-6 pb-6 text-sm">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Diterima Oleh</p>
                <p className="font-bold">{printing.customer_name}</p>
                <p className="text-slate-500">{printing.customer_address || "-"}</p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Referensi Surat Jalan</p>
                <p className="font-bold">{printing.sj_ref || "-"}</p>
                {printing.notes && <p className="text-xs text-slate-500 mt-1">Catatan: {printing.notes}</p>}
              </div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y border-slate-300 text-left text-xs uppercase tracking-widest text-slate-500">
                  <th className="py-2.5 w-10">No</th>
                  <th className="py-2.5">Nama Barang</th>
                  <th className="py-2.5 text-center w-24">Qty</th>
                  <th className="py-2.5 w-24">Satuan</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {printing.items.map((it, i) => (
                  <tr key={i}>
                    <td className="py-3">{i + 1}</td>
                    <td className="py-3 font-semibold">{it.description}</td>
                    <td className="py-3 text-center">{it.qty}</td>
                    <td className="py-3">{it.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="pt-6 text-sm text-slate-600">Demikian surat tanda terima ini dibuat untuk dipergunakan sebagaimana mestinya.</p>
            <div className="document-signatures grid grid-cols-2 gap-6 mt-12 text-center text-xs text-slate-600">
              <div><p>Yang Menyerahkan,</p><SignaturePad storageKey="sig_ttb_penyerah" /><p className="border-t border-slate-400 pt-2 font-bold">{printIdentity.name}</p></div>
              <div><p>Yang Menerima,</p><SignaturePad storageKey="sig_ttb_penerima" /><p className="border-t border-slate-400 pt-2 font-bold">{printing.customer_name}</p></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
